// AI Signal Charts and Visualization Components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Brain,
  Shield,
  BarChart3,
  PieChart as PieChartIcon,
  Radar as RadarIcon,
  RefreshCw
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AISignal, ModelPerformance, RiskMetrics } from "@/lib/ai-signals-api";
import { useSignalHistory, useWinRateByConfidence, useCumulativeReturns, useModelPerformance } from "@/hooks/use-ai-signals";

// Signal History Chart
interface SignalHistoryChartProps {
  signals: AISignal[];
  className?: string;
}

export function SignalHistoryChart({ signals, className }: SignalHistoryChartProps) {
  // Process signals for time series data
  const timeSeriesData = signals
    .filter(signal => signal.created_at)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .slice(-30) // Last 30 signals
    .map((signal, index) => ({
      date: new Date(signal.created_at).toLocaleDateString(),
      confidence: signal.confidence_score * 100,
      risk: signal.risk_score,
      price: signal.current_price,
      signalType: signal.signal_type,
      index
    }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="font-mono font-bold text-primary">
            Confidence: {payload[0]?.value?.toFixed(1)}%
          </p>
          <p className="font-mono text-foreground">
            Risk: {payload[1]?.value}/100
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Signal Performance History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis 
                dataKey="date" 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis 
                yAxisId="confidence"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                domain={[0, 100]}
              />
              <YAxis 
                yAxisId="risk"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                yAxisId="confidence"
                type="monotone" 
                dataKey="confidence" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "hsl(var(--primary))", strokeWidth: 2 }}
              />
              <Line 
                yAxisId="risk"
                type="monotone" 
                dataKey="risk" 
                stroke="hsl(var(--destructive))" 
                strokeWidth={2}
                dot={{ fill: "hsl(var(--destructive))", strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Model Performance Chart
interface ModelPerformanceChartProps {
  className?: string;
}

export function ModelPerformanceChart({ className }: ModelPerformanceChartProps) {
  const { models, isLoading } = useModelPerformance();

  const chartData = models.slice(0, 5).map(model => ({
    name: model.model_name,
    accuracy: model.accuracy_rate,
    sharpeRatio: model.sharpe_ratio,
    totalReturn: model.total_return,
    totalSignals: model.total_signals
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
          <p className="font-semibold mb-2">{label}</p>
          <p className="text-sm text-success">
            Accuracy: {data.accuracy.toFixed(1)}%
          </p>
          <p className="text-sm text-primary">
            Sharpe Ratio: {data.sharpeRatio.toFixed(2)}
          </p>
          <p className="text-sm text-warning">
            Total Return: {data.totalReturn >= 0 ? '+' : ''}{data.totalReturn.toFixed(2)}%
          </p>
          <p className="text-sm text-muted-foreground">
            Total Signals: {data.totalSignals}
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Model Performance Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="w-5 h-5" />
          Model Performance Comparison
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                domain={[0, 100]}
              />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="accuracy"
                fill="hsl(var(--success))"
                radius={[0, 4, 4, 0]}
                name="Accuracy %"
              />
              <Bar
                dataKey="totalReturn"
                fill="hsl(var(--warning))"
                radius={[0, 4, 4, 0]}
                name="Total Return %"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Signal Distribution Chart
interface SignalDistributionChartProps {
  signals: AISignal[];
  className?: string;
}

export function SignalDistributionChart({ signals, className }: SignalDistributionChartProps) {
  // Signal type distribution
  const signalTypeData = [
    {
      name: 'Buy',
      value: signals.filter(s => s.signal_type === 'BUY').length,
      color: 'hsl(var(--success))'
    },
    {
      name: 'Sell',
      value: signals.filter(s => s.signal_type === 'SELL').length,
      color: 'hsl(var(--destructive))'
    },
    {
      name: 'Hold',
      value: signals.filter(s => s.signal_type === 'HOLD').length,
      color: 'hsl(var(--warning))'
    }
  ];

  // Confidence level distribution
  const confidenceData = [
    {
      name: 'High',
      value: signals.filter(s => s.confidence_level === 'HIGH').length,
      color: 'hsl(var(--success))'
    },
    {
      name: 'Medium',
      value: signals.filter(s => s.confidence_level === 'MEDIUM').length,
      color: 'hsl(var(--warning))'
    },
    {
      name: 'Low',
      value: signals.filter(s => s.confidence_level === 'LOW').length,
      color: 'hsl(var(--destructive))'
    }
  ];

  const totalSignals = signals.length;

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4", className)}>
      {/* Signal Type Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <PieChartIcon className="w-5 h-5" />
            Signal Type Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={signalTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {signalTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} signals (${((value as number / totalSignals) * 100).toFixed(1)}%)`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {signalTypeData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm">{item.name}</span>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{item.value}</div>
                  <div className="text-xs text-muted-foreground">
                    {totalSignals > 0 ? ((item.value / totalSignals) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confidence Level Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5" />
            Confidence Level Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={confidenceData}>
                <defs>
                  <linearGradient id="confidenceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, name) => [`${value} signals`, 'Count']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#confidenceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {confidenceData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm">{item.name} Confidence</span>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{item.value}</div>
                  <div className="text-xs text-muted-foreground">
                    {totalSignals > 0 ? ((item.value / totalSignals) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Risk Metrics Radar Chart
interface RiskMetricsChartProps {
  riskMetrics: RiskMetrics | null;
  className?: string;
}

export function RiskMetricsChart({ riskMetrics, className }: RiskMetricsChartProps) {
  if (!riskMetrics) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RadarIcon className="w-5 h-5" />
            Risk Metrics Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Risk Metrics Yet</h3>
            <p className="text-muted-foreground max-w-sm">
              Risk metrics will be available once you have trading data.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const radarData = [
    { subject: 'Portfolio Risk', score: riskMetrics.overall_portfolio_risk, fullMark: 100 },
    { subject: 'Concentration', score: riskMetrics.signal_concentration_risk, fullMark: 100 },
    { subject: 'Market Correlation', score: riskMetrics.market_correlation_risk, fullMark: 100 },
    { subject: 'Volatility', score: riskMetrics.volatility_risk, fullMark: 100 },
    { subject: 'Liquidity', score: riskMetrics.liquidity_risk, fullMark: 100 }
  ];

  const getRiskColor = (score: number) => {
    if (score <= 30) return 'hsl(var(--success))';
    if (score <= 60) return 'hsl(var(--warning))';
    return 'hsl(var(--destructive))';
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Risk Metrics Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--muted))" />
              <PolarAngleAxis 
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                className="text-xs"
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickCount={5}
              />
              <Radar
                name="Risk Score"
                dataKey="score"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Risk Breakdown */}
        <div className="mt-6 space-y-3">
          <h4 className="font-semibold">Risk Breakdown</h4>
          {riskMetrics.risk_score_breakdown.map((factor, index) => (
            <div key={index} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>{factor.factor}</span>
                <span className={cn(
                  "font-mono",
                  factor.impact === 'HIGH' ? 'text-destructive' :
                  factor.impact === 'MEDIUM' ? 'text-warning' :
                  'text-success'
                )}>
                  {factor.score}/100 ({factor.impact})
                </span>
              </div>
              <Progress value={factor.score} className="h-1" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Win Rate by Confidence Level Chart
interface WinRateByConfidenceChartProps {
  className?: string;
}

export function WinRateByConfidenceChart({ className }: WinRateByConfidenceChartProps) {
  const { winRateData, isLoading } = useWinRateByConfidence();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
          <p className="font-semibold mb-2">{label} Confidence</p>
          <p className="text-sm text-success">
            Win Rate: {data.winRate.toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground">
            Wins: {data.wins}/{data.total} signals
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Win Rate by Confidence Level
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5" />
          Win Rate by Confidence Level
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={winRateData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis
                dataKey="level"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="winRate"
                fill="hsl(var(--success))"
                radius={[4, 4, 0, 0]}
                name="Win Rate %"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Cumulative Returns Chart
interface CumulativeReturnsChartProps {
  className?: string;
}

export function CumulativeReturnsChart({ className }: CumulativeReturnsChartProps) {
  const { cumulativeReturns, isLoading } = useCumulativeReturns();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
          <p className="text-sm text-muted-foreground mb-1">{new Date(data.date).toLocaleDateString()}</p>
          <p className="font-mono font-bold text-primary">
            Cumulative Return: {data.cumulativeReturn >= 0 ? '+' : ''}{data.cumulativeReturn.toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Cumulative Returns Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Cumulative Returns Over Time
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cumulativeReturns}>
              <defs>
                <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--success))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                tickFormatter={(value) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulativeReturn"
                stroke="hsl(var(--success))"
                strokeWidth={2}
                fill="url(#cumulativeGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Signal Performance Over Time Chart
interface SignalPerformanceOverTimeChartProps {
  className?: string;
}

export function SignalPerformanceOverTimeChart({ className }: SignalPerformanceOverTimeChartProps) {
  const { signalHistory, isLoading } = useSignalHistory();

  const timeSeriesData = signalHistory
    .filter(item => item.date && item.price !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-50) // Last 50 data points
    .map((item, index) => ({
      date: new Date(item.date).toLocaleDateString(),
      price: item.price,
      confidence: item.confidence * 100,
      accuracy: item.accuracy ? item.accuracy * 100 : 0,
      index
    }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="font-mono font-bold text-primary">
            Price: ${payload[0]?.value?.toFixed(2)}
          </p>
          <p className="font-mono text-foreground">
            Confidence: {payload[1]?.value?.toFixed(1)}%
          </p>
          <p className="font-mono text-success">
            Accuracy: {payload[2]?.value?.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Signal Performance Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Signal Performance Over Time
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                yAxisId="price"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                yAxisId="percentage"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="price"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 3 }}
              />
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="confidence"
                stroke="hsl(var(--warning))"
                strokeWidth={2}
                dot={{ fill: "hsl(var(--warning))", strokeWidth: 2, r: 3 }}
              />
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="accuracy"
                stroke="hsl(var(--success))"
                strokeWidth={2}
                dot={{ fill: "hsl(var(--success))", strokeWidth: 2, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Signal Performance Summary Card
interface SignalPerformanceSummaryProps {
  signals: AISignal[];
  className?: string;
}

export function SignalPerformanceSummary({ signals, className }: SignalPerformanceSummaryProps) {
  const activeSignals = signals.filter(s => s.status === 'ACTIVE').length;
  const executedSignals = signals.filter(s => s.status === 'EXECUTED').length;
  const avgConfidence = signals.reduce((sum, s) => sum + s.confidence_score, 0) / signals.length || 0;
  const avgRisk = signals.reduce((sum, s) => sum + s.risk_score, 0) / signals.length || 0;
  
  const buySignals = signals.filter(s => s.signal_type === 'BUY').length;
  const sellSignals = signals.filter(s => s.signal_type === 'SELL').length;
  const holdSignals = signals.filter(s => s.signal_type === 'HOLD').length;

  const highConfidenceSignals = signals.filter(s => s.confidence_level === 'HIGH').length;
  const successRate = executedSignals > 0 ? (signals.filter(s => s.performance_metrics?.profit_loss && s.performance_metrics.profit_loss > 0).length / executedSignals) * 100 : 0;

  const stats = [
    { 
      label: "Active Signals", 
      value: activeSignals, 
      change: 0,
      icon: Activity,
      color: "text-primary"
    },
    { 
      label: "Executed Signals", 
      value: executedSignals, 
      change: 0,
      icon: Target,
      color: "text-success"
    },
    { 
      label: "Avg Confidence", 
      value: `${(avgConfidence * 100).toFixed(1)}%`, 
      change: 0,
      icon: Brain,
      color: "text-primary"
    },
    { 
      label: "Avg Risk Score", 
      value: avgRisk.toFixed(1), 
      change: 0,
      icon: Shield,
      color: "text-warning"
    },
    { 
      label: "Buy Signals", 
      value: buySignals, 
      change: 0,
      icon: TrendingUp,
      color: "text-success"
    },
    { 
      label: "Sell Signals", 
      value: sellSignals, 
      change: 0,
      icon: TrendingDown,
      color: "text-destructive"
    },
    { 
      label: "High Confidence", 
      value: highConfidenceSignals, 
      change: 0,
      icon: Target,
      color: "text-success"
    },
    { 
      label: "Success Rate", 
      value: `${successRate.toFixed(1)}%`, 
      change: 0,
      icon: BarChart3,
      color: "text-primary"
    }
  ];

  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-4", className)}>
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon className={cn("w-5 h-5", stat.color)} />
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-mono font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}